import { useEffect, useState } from 'react'
import DatabaseClient, { CustomerUsageData } from '../../utils/database-client'
import { formatCurrency } from '../../utils/utils'
import { Button } from '../ui/Button'
import { Card } from '../ui/Card'
import { useCustomerStore } from '../../stores/customerStore'
import { AllCustomersGrid } from './AllCustomersGrid'
import apiClient from '../../api/apiClient'
import { AxiosError } from 'axios'
import { CloudAdvisorRecommendations } from './CloudAdvisorRecommendations'

// 오늘 날짜인지 확인하는 함수
const isToday = (dateString: string): boolean => {
  const today = new Date()
  const date = new Date(dateString)
  return today.toDateString() === date.toDateString()
}

export function ExcelDataDashboard() {
  const { selectedCustomer } = useCustomerStore()
  const [customerData, setCustomerData] = useState<CustomerUsageData | null>(null)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState<string>('')
  const [dbClient] = useState(() => new DatabaseClient())
  const [currentYearMonth, setCurrentYearMonth] = useState(() => {
    const now = new Date()
    const year = now.getFullYear()
    const month = String(now.getMonth() + 1).padStart(2, '0')
    return `${year}-${month}`
  })

  useEffect(() => {
    if (selectedCustomer && selectedCustomer !== 'all') {
      loadCustomerData(selectedCustomer, currentYearMonth)
    }
  }, [selectedCustomer, currentYearMonth])

  // 월별 네비게이션 함수들
  const goToPreviousMonth = () => {
    const [year, month] = currentYearMonth.split('-').map(Number)
    const date = new Date(year, month - 1, 1)
    date.setMonth(date.getMonth() - 1)
    const newYearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    setCurrentYearMonth(newYearMonth)
  }

  const goToNextMonth = () => {
    const [year, month] = currentYearMonth.split('-').map(Number)
    const date = new Date(year, month - 1, 1)
    date.setMonth(date.getMonth() + 1)
    const newYearMonth = `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`
    setCurrentYearMonth(newYearMonth)
  }

  const loadCustomerData = async (customerName: string, yearMonth?: string) => {
    setLoading(true)
    setError('')
    try {
      // 월별 데이터가 요청된 경우 Spring Boot API 사용
      if (yearMonth) {
        const response = await apiClient.get(
          `/api/customers/${encodeURIComponent(customerName)}/usage/monthly?yearMonth=${yearMonth}`
        )

        const monthlyData = response.data

        // Spring Boot API 응답을 기존 형식으로 변환
        const convertedData: CustomerUsageData = {
          customerInfo: monthlyData.customerInfo || {
            고객명: customerName,
            테넌시명: customerName,
            계약금액: 0,
            계약일: '',
            만료일: '',
            생성일시: '',
            CSI: '',
            Opportunity_ID: '',
            Subscription_Plan_Number: '',
            영업대표: '',
            클라우드계정관리자: '',
            잔액소진예상일: ''
          },
          dailyUsageData: monthlyData.dailyData?.map((day: any) => ({
            날짜: day.date,
            사용량: day.totalUsage,
            잔액: day.remainingBalance
          })) || [],
          serviceUsageData: monthlyData.dailyData?.map((day: any) => ({
            날짜: day.date,
            사용량: day.totalUsage,
            잔액: day.remainingBalance,
            ...day.serviceBreakdown
          })) || []
        }

        setCustomerData(convertedData)
      } else {
        // 기존 방식 (전체 데이터)
        const data = await dbClient.getCustomerDataByDisplayName(customerName)
        setCustomerData(data)
      }
    } catch (err) {
      console.error('Failed to load customer data:', err)
      if (err instanceof AxiosError) {
        setError(`고객 데이터를 불러오는데 실패했습니다: ${err.response?.data?.message || err.message}`)
      } else {
        setError('고객 데이터를 불러오는데 실패했습니다.')
      }
    } finally {
      setLoading(false)
    }
  }



  if (error) {
    return (
      <div className="text-center py-8">
        <div className="text-red-600 mb-4">{error}</div>
        <Button onClick={() => window.location.reload()}>다시 시도</Button>
      </div>
    )
  }

  // "전체 고객사" 선택 시 - AllCustomersGrid 표시
  if (selectedCustomer === 'all') {
    return <AllCustomersGrid />
  }

  // 선택된 고객이 없을 경우
  if (!selectedCustomer) {
    return (
      <div className="text-center py-12">
        <h2 className="text-xl font-semibold text-gray-600 dark:text-gray-400 mb-4">고객사를 선택해주세요</h2>
        <p className="text-gray-500 dark:text-gray-500">좌측 사이드바에서 고객사를 선택하세요.</p>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {loading && (
        <div className="text-center py-8">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary-600 dark:border-primary-500 mx-auto"></div>
          <p className="mt-2 text-gray-600 dark:text-gray-400">데이터를 불러오는 중...</p>
        </div>
      )}

      {customerData && !loading && (
        <>
          {/* 고객 정보 */}
          <Card>
            <div className="p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold mb-4 dark:text-gray-100">고객 정보</h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">고객명</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.고객명}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">테넌시명</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.테넌시명}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">계약금액</p>
                  <p className="font-medium text-sm md:text-lg text-primary-600 dark:text-primary-500">{formatCurrency(customerData.customerInfo.계약금액)}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mt-4">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">계약일</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.계약일}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">만료일</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.만료일}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">생성일시</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.생성일시}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3 md:gap-4 mt-4">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">CSI</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.CSI}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Opportunity ID</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.Opportunity_ID}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">Subscription Plan Number</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.Subscription_Plan_Number}</p>
                </div>
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 md:gap-4 mt-4">
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">영업대표</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.영업대표}</p>
                </div>
                <div>
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400">클라우드 계정 관리자</p>
                  <p className="font-medium text-sm md:text-base dark:text-gray-200">{customerData.customerInfo.클라우드계정관리자}</p>
                </div>
              </div>
            </div>
          </Card>

          {/* 비용 분석 */}
          <Card>
            <div className="p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold mb-4 dark:text-gray-100">
                비용레포트
                <span className="text-sm font-normal text-gray-500 dark:text-gray-400 ml-2">
                  (조회날짜: {customerData.dailyUsageData[0]?.날짜 || 'N/A'})
                </span>
              </h2>

              {/* 현재 잔액 */}
              <div className="text-center mb-6">
                <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-2">말일 기준 잔액</p>
                <p className="text-2xl md:text-4xl font-bold text-primary-600 dark:text-primary-500 mb-2">
                  {formatCurrency(customerData.dailyUsageData[0]?.잔액 || 0)}
                </p>
              </div>

              {/* 일평균 사용량, 월 사용량, 갱신 예상일 */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 mt-6">
                <div className="text-center">
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-1">일평균 사용량</p>
                  <p className="text-lg md:text-xl font-semibold text-orange-600 dark:text-orange-500">
                    {(() => {
                      const recentData = customerData.dailyUsageData.slice(0, 7); // 최근 7일
                      if (recentData.length === 0) return 'N/A';

                      const totalUsage = recentData.reduce((sum, item) => sum + item.사용량, 0);
                      const avgUsage = totalUsage / recentData.length;
                      return formatCurrency(avgUsage);
                    })()}
                  </p>
                </div>
                <div className="text-center">
                  <p className="text-xs md:text-sm text-gray-600 dark:text-gray-400 mb-1">월 사용량</p>
                  <p className="text-lg md:text-xl font-semibold text-orange-600 dark:text-orange-500">
                    {(() => {
                      const recentData = customerData.dailyUsageData.slice(0, 30); // 최근 30일
                      if (recentData.length === 0) return 'N/A';

                      const totalUsage = recentData.reduce((sum, item) => sum + item.사용량, 0);
                      return formatCurrency(totalUsage);
                    })()}
                  </p>
                </div>
                <div className="text-center p-4 bg-red-50 dark:bg-red-900/10 rounded-lg border-2 border-red-400 dark:border-red-800/50">
                  <p className="text-xs md:text-sm text-gray-700 dark:text-gray-400 mb-1 font-semibold">갱신 예상일</p>
                  <p className="text-xl md:text-2xl font-bold text-red-600 dark:text-red-400">
                    {customerData.customerInfo.잔액소진예상일 || '계산 불가'}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          <CloudAdvisorRecommendations customerName={selectedCustomer} />

          {/* 월별 네비게이션 */}
          <Card>
            <div className="p-4 md:p-6">
              <div className="flex items-center justify-center space-x-4">
                <Button
                  onClick={goToPreviousMonth}
                  disabled={loading}
                  className="px-3 py-1"
                >
                  ◀
                </Button>

                <div className="text-center">
                  <p className="text-lg font-semibold dark:text-gray-100">
                    {currentYearMonth.replace('-', '년 ') + '월'}
                  </p>
                </div>

                <Button
                  onClick={goToNextMonth}
                  disabled={loading}
                  className="px-3 py-1"
                >
                  ▶
                </Button>
              </div>
            </div>
          </Card>

          {/* 서비스별 사용량 (피벗 테이블) */}
          <Card>
            <div className="p-4 md:p-6">
              <h2 className="text-lg md:text-xl font-semibold mb-4 dark:text-gray-100">서비스별 사용량</h2>
              <div className="overflow-x-auto">
                {customerData.serviceUsageData.length > 0 && (() => {
                  // 고객이 실제 사용한 서비스 목록 추출
                  const usedServices = new Set<string>();
                  customerData.serviceUsageData.forEach(item => {
                    Object.keys(item).forEach(key => {
                      if (key !== '날짜' && key !== '사용량' && key !== '잔액' && item[key] > 0) {
                        usedServices.add(key);
                      }
                    });
                  });

                  const serviceList = Array.from(usedServices).sort();

                  return (
                    <table className="w-full text-xs md:text-sm">
                      <thead>
                        <tr className="border-b dark:border-gray-700">
                          <th className="text-left py-2 px-1 md:px-2 dark:text-gray-200">날짜</th>
                          {serviceList.map(service => (
                            <th key={service} className="text-right py-2 px-1 md:px-2 dark:text-gray-200">{service}</th>
                          ))}
                          <th className="text-right py-2 px-1 md:px-2 dark:text-gray-200">사용량</th>
                          <th className="text-right py-2 px-1 md:px-2 dark:text-gray-200">잔액</th>
                        </tr>
                      </thead>
                      <tbody>
                        {customerData.serviceUsageData.map((item: any, index: number) => (
                          <tr key={index} className="border-b dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700">
                            <td className="py-2 px-1 md:px-2 dark:text-gray-300">{item.날짜}</td>
                            {serviceList.map(service => (
                              <td key={service} className="text-right py-2 px-1 md:px-2 dark:text-gray-300">
                                {formatCurrency(item[service] || 0)}
                              </td>
                            ))}
                            <td className="text-right py-2 px-1 md:px-2 font-bold text-red-600 dark:text-red-500">
                              -{formatCurrency(item.사용량)}
                            </td>
                            <td className="text-right py-2 px-1 md:px-2 font-bold text-gray-600 dark:text-gray-400">
                              <span className={index === 0 ? 'bg-primary-400 text-white px-1 rounded' : ''}>
                                {formatCurrency(item.잔액)}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  );
                })()}
              </div>
            </div>
          </Card>
        </>
      )}
    </div>
  )
}
